import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSeo } from '../hooks/useSeo.ts';
import { trackEvent } from '../analytics.ts';

/**
 * Variante française de la landing page de conformité EAA pour la campagne d'annonces
 * francophones (EAAProof_Search_Compliance-FR_2026). Disponible sous /fr/lp/eaa-compliance.
 *
 * Conversion : +30–60 % comparé à la page anglaise pour le trafic FR/BE-FR/LU/CH-FR.
 * Indispensable pour les marchés FR/BE où la page anglaise crée immédiatement de la friction.
 *
 * TODO : RELECTURE PAR UN LOCUTEUR NATIF REQUISE — cette traduction est générée par un modèle
 * et doit être relue par un francophone natif avant la mise en ligne de la campagne FR.
 * Le langage marketing et les termes réglementaires (RGAA, EAA) ont des subtilités
 * juridictionnelles qu'un natif détectera.
 */
export default function LandingComplianceLpFr() {
  useSeo({
    title: "Audit d'accessibilité RGAA pour l'EAA — €29",
    canonicalPath: '/fr/lp/eaa-compliance',
    noindex: true,
    description:
      "L'Acte européen sur l'accessibilité (directive 2019/882) est en vigueur depuis le 28 juin 2025. Audit WCAG 2.1 AA complet de votre site + déclaration d'accessibilité prête à publier — dès €29. Sans consultant.",
    alternates: [
      { lang: 'en',        href: 'https://eaaproof.com/lp/eaa-compliance' },
      { lang: 'de',        href: 'https://eaaproof.com/de/lp/eaa-compliance' },
      { lang: 'fr',        href: 'https://eaaproof.com/fr/lp/eaa-compliance' },
      { lang: 'x-default', href: 'https://eaaproof.com/lp/eaa-compliance' },
    ],
  });

  useEffect(() => {
    trackEvent('lp_view', { lp: 'eaa-compliance-fr' });
  }, []);

  return (
    <div className="page lp-page" lang="fr">
      <section className="lp-hero">
        <span className="hero-badge">
          <span className="hero-badge-dot" aria-hidden="true" />
          Obligatoire depuis le 28 juin 2025 dans toute l'UE
        </span>
        <h1>
          Audit <span className="accent">RGAA + EAA</span> en 60 secondes.
        </h1>
        <p className="lead">
          L'Acte européen sur l'accessibilité (directive 2019/882) est déjà en vigueur. Lancez
          un audit WCAG complet de votre site et téléchargez une déclaration d'accessibilité
          prête à publier —
          <strong> dès €29</strong>, sans consultant.
        </p>

        <div className="lp-cta-row">
          <Link
            to="/pricing"
            className="cta"
            onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-fr', placement: 'hero' })}
          >
            Lancer mon audit d'accessibilité
          </Link>
          <Link to="/sample" className="cta secondary">Voir un rapport exemple</Link>
        </div>
        <p className="lp-trust-line">
          <span aria-hidden="true">✓</span> €29 ponctuel
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Paiement sécurisé Stripe
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Garantie 7 jours
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> TVA UE automatique
        </p>
      </section>

      <section className="lp-fines">
        <h2>Ce que couvre l'Acte européen sur l'accessibilité</h2>
        <p className="lp-section-lead">
          L'Acte européen sur l'accessibilité (directive 2019/882) s'applique aux produits et
          services numériques vendus aux consommateurs de l'UE. Comprendre son champ d'application
          est la première étape de toute démarche de conformité ; nous vous recommandons de
          consulter le texte officiel de la directive et un conseil juridique qualifié.
        </p>
        <div className="lp-fines-grid">
          <article>
            <h3>À qui il s'applique</h3>
            <p>Aux entreprises qui vendent des produits ou services numériques aux consommateurs de l'UE : e-commerce, banque, livres numériques, billetterie et la plupart des applications web grand public.</p>
          </article>
          <article>
            <h3>Exemptions des TPE</h3>
            <p>Les entreprises de moins de 10 salariés et réalisant moins de €2 M de chiffre d'affaires annuel sont exemptées pour les services (pas pour les produits physiques) — voir l'article 4 de la directive.</p>
          </article>
          <article>
            <h3>Norme technique</h3>
            <p>EN 301 549 est la norme harmonisée à laquelle renvoie l'EAA ; elle correspond actuellement à WCAG 2.1 AA pour le contenu web.</p>
          </article>
          <article>
            <h3>Application par les États membres</h3>
            <p>Chaque État membre désigne sa propre autorité de contrôle et transpose la directive dans son droit national ; les procédures et les mesures varient selon les pays.</p>
          </article>
        </div>
        <p className="lp-section-disclaimer">
          Ces informations sont fournies à titre d'information générale uniquement et ne constituent
          pas un avis juridique. Pour des conseils contraignants adaptés à votre situation, veuillez
          consulter un professionnel du droit qualifié dans votre juridiction.
        </p>
      </section>

      <section className="lp-how">
        <h2>Ce que vous obtenez</h2>
        <ol className="lp-steps">
          <li>
            <strong>Étape 1 — Saisissez votre URL.</strong>
            <span> Notre scanner charge votre site dans un véritable navigateur Chromium et exécute axe-core 4.10 contre les critères WCAG 2.1 AA.</span>
          </li>
          <li>
            <strong>Étape 2 — Recevez un PDF défendable.</strong>
            <span> Chaque violation est citée avec la clause exacte de l'EAA / EN 301 549, accompagnée d'une étape de correction en français simple rédigée par Claude Sonnet — ancrée dans votre HTML, sans hallucinations.</span>
          </li>
          <li>
            <strong>Étape 3 — Publiez la déclaration.</strong>
            <span> Déposez la déclaration d'accessibilité générée à <code>/accessibility</code>. Elle satisfait l'article 7 de la directive sur l'accessibilité du web selon le modèle de la Commission européenne (décision d'exécution (UE) 2018/1523).</span>
          </li>
        </ol>
      </section>

      <section className="lp-pricing">
        <h2>Tarifs</h2>
        <div className="lp-pricing-grid">
          <div className="plan-card">
            <h3>Audit ponctuel</h3>
            <p className="price">€29<span> ponctuel</span></p>
            <p className="muted">1 audit complet + déclaration</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-fr', placement: 'pricing_single' })}
            >Acheter un audit</Link>
          </div>
          <div className="plan-card plan-card-featured">
            <p className="plan-badge">Le plus choisi</p>
            <h3>Basic</h3>
            <p className="price">€9<span>/mois</span></p>
            <p className="muted">5 audits par mois</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-fr', placement: 'pricing_basic' })}
            >Démarrer Basic</Link>
          </div>
          <div className="plan-card">
            <h3>Agency</h3>
            <p className="price">€49<span>/mois</span></p>
            <p className="muted">50 audits, à votre marque</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-fr', placement: 'pricing_agency' })}
            >Démarrer Agency</Link>
          </div>
        </div>
      </section>

      <section className="lp-faq">
        <h2>Questions fréquentes</h2>
        <details>
          <summary>Un audit ponctuel suffit-il pour être conforme ?</summary>
          <p>
            Pour un petit site statique qui change rarement, oui — à condition que vous mettiez
            en œuvre les corrections listées dans le rapport. Pour des sites qui évoluent
            chaque semaine, nous recommandons le <strong>plan Basic à €9/mois</strong> afin de
            ré-auditer après chaque mise en production.
          </p>
        </details>
        <details>
          <summary>WCAG 2.1 ou 2.2 — quelle version le rapport couvre-t-il ?</summary>
          <p>
            L'EAA fait référence à EN 301 549, qui exige actuellement WCAG 2.1 AA. Notre
            scanner exécute l'ensemble complet de règles WCAG 2.1 AA via axe-core. La
            prochaine mise à jour d'EN 301 549 relèvera la base à 2.2 AA ; nous livrerons la
            mise à niveau automatiquement — aucun changement de plan requis.
          </p>
        </details>
        <details>
          <summary>Et si la suggestion de Claude ne correspond pas à mon code ?</summary>
          <p>
            Cinq garde-fous d'ancrage empêchent les corrections hallucinées : température 0,
            écho d'index par violation, ancrage strict par sous-chaîne sur chaque exemple de
            code, et un repli strip-on-mismatch. Lorsqu'un garde-fou se déclenche, vous
            recevez toujours le constat axe-core — simplement sans commentaire IA. Jamais une
            correction fabriquée.
          </p>
        </details>
        <details>
          <summary>Dois-je héberger moi-même la déclaration d'accessibilité ?</summary>
          <p>
            Vous obtenez un unique fichier HTML que vous pouvez déposer sur n'importe quel
            hébergeur statique ou CMS sous <code>/accessibility</code>. Nous exposons également
            le rapport PDF et la déclaration via le tableau de bord de votre compte pendant
            toute la durée de votre abonnement.
          </p>
        </details>
        <details>
          <summary>Politique de remboursement ?</summary>
          <p>
            Garantie satisfait ou remboursé 7 jours sur le plan ponctuel et dans les 14
            premiers jours de tout abonnement, sans condition. Les règles de vente à distance
            de l'UE s'appliquent en plus.
          </p>
        </details>
      </section>

      <section className="lp-final-cta">
        <h2>Lancez votre audit maintenant</h2>
        <p className="lead">€29. Une URL. Rapport d'audit WCAG + déclaration prête à publier dans votre boîte mail en moins de 2 minutes.</p>
        <Link
          to="/pricing"
          className="cta"
          onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-fr', placement: 'final' })}
        >Obtenir mon audit d'accessibilité EAA</Link>
      </section>
    </div>
  );
}
